<template>
  <div v-if="company" class="container-fluid pb-6">
    <EacDashboardHeaderComponent
      :company
      :isAdmin
      :activeSection="activePrimarySection"
      :activeSettingsTab="resolvedSettingsTab"
      :inventoryLabel="$t('dashboard.companyData.manage_inventory')"
      :availableGos="portfolioItemsStore.totalAvailableGOs"
      :pendingPayout
      :isLoading
      @select:section="setPrimarySection"
      @open:settings-tab="openSettingsTab"
    />

    <OnboardingCallCardComponent
      v-if="!isAdmin"
      :key="`onboarding-call-${company.id}-${billingUpdates}`"
      :company="company"
      organisation="trader"
      class="mb-4"
    />

    <div class="card mb-4">
      <div class="card-body">
        <TabsContainer
          v-if="company.id && activePrimarySection === 'eacs'"
          :tabs="eacTabs"
          @change:tab="handleEacTabChange"
          :activeTab="resolvedEacTab"
        >
          <template #tab-overview>
            <FilterablePortfolioChartPanel />
          </template>
          <template #tab-receive>
            <ReceiveGoComponent :company="company" />
          </template>
          <template #tab-send>
            <SendGoComponent :company-id="company.id!" />
          </template>
          <template #tab-portfolios>
            <PortfoliosListComponent />
          </template>
          <template #tab-cancel>
            <CancelGoComponent :company-id="company.id!" :company="company" />
          </template>
          <template #tab-buy>
            <BuyGoComponent :company-id="company.id!" :company="company" />
          </template>
          <template #tab-sell>
            <SellGoComponent :company="company" />
          </template>
          <template #tab-cancellation-documents>
            <CancellationStatementsComponent
              :companies="[company]"
              :is_admin="isAdmin || false"
            />
          </template>
          <template #tab-company-orders>
            <CompanyOrdersComponent
              :companies="[company]"
              :is_admin="isAdmin || false"
            />
          </template>
        </TabsContainer>

        <TabsContainer
          v-else-if="company.id && activePrimarySection === 'settings'"
          :tabs="settingsTabs"
          @change:tab="handleSettingsTabChange"
          :activeTab="resolvedSettingsTab"
        >
          <template #tab-settings>
            <SettingsComponent
              v-if="company.id"
              :companyId="company.id!"
              :company="company"
            />
          </template>
          <template #tab-submit-invoice>
            <RequestInvoiceComponent
              :company="company"
              :pendingInvoiceItems="pendingInvoiceItems"
              :defaultInvoiceNumber="defaultInvoiceNumber"
              :monthSplit="monthSplit"
              @invoice-requested="handleInvoiceRequested"
            />
          </template>
          <template #tab-invoice-history>
            <InvoiceHistoryComponent
              v-if="company.id"
              :company_id="company.id!"
              :companies="[company]"
              :is_admin="isAdmin || false"
            />
          </template>
          <template #tab-documents>
            <UnifiedDocumentsTable
              :companies="[company]"
              :isAdmin="isAdmin || false"
              :showCompanyColumn="false"
            />
          </template>
        </TabsContainer>
      </div>
    </div>

    <!-- One blocking onboarding: the qualification questions, the Platform Agreement, then the plan. -->
    <CorporateOnboardingModal
      v-if="!isAdmin"
      :key="`onboarding-${company.id}`"
      :company="company"
      :needs-agreement="mustAcceptTerms"
      organisation="trader"
      @paid="billingUpdates++"
      @request:onboarding-call="billingUpdates++"
    />
  </div>
  <loading-overlay-component v-if="isLoading"></loading-overlay-component>
</template>

<script setup lang="ts">
import CorporateOnboardingModal from "@/billing/CorporateOnboardingModal.vue";
import OnboardingCallCardComponent from "@/billing/OnboardingCallCardComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useTabs, type Tab } from "@/composables/useTabs";
import EacDashboardHeaderComponent, {
  type EacDashboardSection,
} from "@/dashboard/common/EacDashboardHeaderComponent.vue";
import ReceiveGoComponent from "@/dashboard/inventory_management/accounts/ReceiveGoComponent.vue";
import BuyGoComponent from "@/dashboard/inventory_management/buy/BuyGoComponent.vue";
import CancelGoComponent from "@/dashboard/inventory_management/cancel/CancelGoComponent.vue";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import CompanyOrdersComponent from "@/dashboard/inventory_management/company_orders/CompanyOrdersComponent.vue";
import FilterablePortfolioChartPanel from "@/dashboard/inventory_management/overview/FilterablePortfolioChartPanel.vue";
import PortfoliosListComponent from "@/dashboard/inventory_management/portfolios/PortfoliosListComponent.vue";
import SellGoComponent from "@/dashboard/inventory_management/sell/SellGoComponent.vue";
import SendGoComponent from "@/dashboard/inventory_management/send/SendGoComponent.vue";
import CancellationStatementsComponent from "@/dashboard/invoice_management/CancellationStatementsComponent.vue";
import UnifiedDocumentsTable from "@/dashboard/invoice_management/documents/UnifiedDocumentsTable.vue";
import SettingsComponent from "@/dashboard/settings_management/SettingsComponent.vue";
import InvoiceHistoryComponent from "@/money/invoice/InvoiceHistoryComponent.vue";
import RequestInvoiceComponent from "@/money/invoice/RequestInvoiceComponent.vue";
import { usePendingInvoiceItems } from "@/money/invoice/usePendingInvoiceItems";
import type { Company } from "@/types";
import { computed, onMounted, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = defineProps<{
  company: Company;
  isAdmin: boolean;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler: handleSuccess } =
  useGlobalHandlers();

const isLoading = ref(false);

const portfolioItemsStore = usePortfolioItemsStore();
const {
  pendingInvoiceItems,
  defaultInvoiceNumber,
  monthSplit,
  pendingPayout,
  canSubmitInvoice,
  fetchPendingInvoiceItems,
  clearPendingInvoiceItems,
} = usePendingInvoiceItems();

// Tab order: Overview first
const eacTabs = computed(() => {
  const tabs: Array<Tab> = [
    {
      id: "overview",
      name: t("tradingCenter.tabs.overview"),
      icon: "chart-pie",
    },
    {
      id: "receive",
      name: t("common.actions.receive"),
      icon: "inbox",
    },
    {
      id: "send",
      name: t("common.actions.send"),
      icon: "paper-plane",
    },
    {
      id: "portfolios",
      name: t("common.actions.portfolios"),
      icon: "layer-group",
    },
    {
      id: "cancel",
      name: t("common.actions.cancel"),
      icon: "ban",
    },
  ];

  if (props.company.feature_flags?.buy_EACs_enabled) {
    tabs.push({
      id: "buy",
      name: t("common.actions.buy"),
      icon: "shopping-cart",
    });
  }

  tabs.push(
    {
      id: "sell",
      name: t("common.actions.sell"),
      icon: "dollar-sign",
    },
    {
      id: "cancellation-documents",
      name: t("dashboard.companyData.cancellation_documents"),
      icon: "file-circle-check",
    },
    {
      id: "company-orders",
      name: t("company_orders.title"),
      icon: "list-ul",
    }
  );

  return tabs;
});

const defaultEacTabId = "overview";

const { activeTab: rawEacTab, setTab: setEacTab } = useTabs("trader-eacs", {
  tabs: eacTabs,
  defaultTabId: defaultEacTabId,
});

const resolvedEacTab = computed(() => rawEacTab.value || defaultEacTabId);

const handleEacTabChange = (tabId: string) => {
  setEacTab(tabId);
};

const settingsTabs = computed<Tab[]>(() => [
  {
    id: "settings",
    name: t("settings.title"),
    icon: "gear",
  },
  ...(canSubmitInvoice.value
    ? [
        {
          id: "submit-invoice",
          name: t("dashboard.companyData.submit_invoice"),
          icon: "file-invoice-dollar",
        },
      ]
    : []),
  {
    id: "invoice-history",
    name: t("dashboard.companyData.invoice_history"),
    icon: "file-invoice-dollar",
  },
  {
    id: "documents",
    name: t("common.documents"),
    icon: "file-alt",
  },
]);

const { activeTab: rawSettingsTab, setTab: setSettingsTab } = useTabs(
  "trader-settings",
  {
    tabs: settingsTabs,
    defaultTabId: "settings",
  }
);

const resolvedSettingsTab = computed(() => rawSettingsTab.value || "settings");

const handleSettingsTabChange = (tabId: string) => {
  setSettingsTab(tabId);
};

const activePrimarySection = ref<EacDashboardSection>("eacs");

const setPrimarySection = (sectionId: EacDashboardSection) => {
  activePrimarySection.value = sectionId;
};

const mustAcceptTerms = computed(() => {
  const hasSigned =
    props.company.membership_agreement_signed_at ||
    props.company.has_registered;
  return !hasSigned && !props.isAdmin;
});

// Bumped when the onboarding modal confirms a payment or opens the call booking, so the onboarding call card reloads
// and shows the new state at once.
const billingUpdates = ref(0);

const handleInvoiceRequested = () => {
  handleSuccess(
    t("dashboard.companyData.invoice_submission_success"),
    t("dashboard.companyData.invoice_paid_within_7_days")
  );
  openSettingsTab("invoice-history");
  clearPendingInvoiceItems();
};

const openSettingsTab = (tabId: string) => {
  setPrimarySection("settings");
  setSettingsTab(tabId);
};

const prefillBuyVolume = ref<number | null>(null);
provide("prefillBuyVolume", prefillBuyVolume);

provide(
  "navigateToInventoryTab",
  (action: "send" | "cancel" | "sell" | "receive" | "portfolios" | "buy") => {
    setPrimarySection("eacs");

    const actionToTab: Record<
      "send" | "cancel" | "sell" | "receive" | "portfolios" | "buy",
      string
    > = {
      send: "send",
      cancel: "cancel",
      sell: "sell",
      receive: "receive",
      portfolios: "portfolios",
      buy: "buy",
    };
    handleEacTabChange(actionToTab[action]);
  }
);

async function loadCompanyData() {
  if (!props.company.id) return;

  isLoading.value = true;
  try {
    portfolioItemsStore.setAllItems([]);
    await Promise.all([
      portfolioItemsStore.fetchItems(props.company.id),
      fetchPendingInvoiceItems(props.company.id).catch(globalErrorHandler),
    ]);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => props.company.id,
  (newId, oldId) => {
    if (newId !== oldId) {
      loadCompanyData();
    }
  }
);

onMounted(() => {
  loadCompanyData();
});
</script>

<style scoped lang="scss">
.container-fluid {
  text-align: left;
  max-width: 1400px;
  margin: 0 auto;
}
</style>
